import React, { useEffect, useState, useCallback } from 'react';
import { LocationDTO, ProgramDTO } from '@peartherapeutics/weaver-api-client';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { Location } from 'history';
import { routes } from 'utils/routeHelper';
import Program from 'utils/program';
import SelectProgram from 'components/common/SelectProgram';
import SelectLocation from 'components/common/SelectLocation';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import styles from './styles.scss';

export interface IProps {
  location: Location;
  selectClinic: (label: string, value: string) => void;
  setProgram: (program: ProgramDTO) => void;
  isManageLocationsTabShown: boolean;
  isManageCliniciansTabShown: boolean;
  isManagePatientsTabShown: boolean;
}

const Tabbar = (props: IProps) => {
  const [clinics, setClinics] = useState<LocationDTO[]>([]);
  const [programs, setPrograms] = useState<ProgramDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const {
    location,
    selectClinic,
    setProgram,
    isManageLocationsTabShown,
    isManageCliniciansTabShown,
    isManagePatientsTabShown,
  } = props;

  const clearSelected = useCallback(() => {
    setIsLoading(true);
    setClinics([]);
    setPrograms([]);
    selectClinic('', '');
    Program.clear();
  }, [selectClinic]);

  const getClinics = async () => {
    try {
      const { data: clinics } = await WeaverApiClientInstance.getAllLocations();
      setClinics(clinics);
      setIsLoading(false);
    } catch (e) {
      ErrorInterceptor.error(e.response.data);
    }
  };

  const getRxLinkedPrograms = useCallback(async () => {
    try {
      const { data: programs } = await WeaverApiClientInstance.getPrograms();
      setPrograms(programs);
      setIsLoading(false);
    } catch (e) {
      ErrorInterceptor.error(e);
    }
  }, []);

  const showClinicPulldown = useCallback(
    (): boolean =>
      [routes.MANAGE_PATIENTS, routes.MANAGE_CLINICIANS].some(
        (item) => item === location.pathname,
      ),
    [location.pathname],
  );

  const handleChangeProgram = () => {
    setIsLoading(true);
    setClinics([]);
    selectClinic('', '');
    getClinics();
  };

  useEffect(() => {
    if (showClinicPulldown()) {
      if (location.pathname === routes.MANAGE_CLINICIANS) {
        getClinics();
      }
    }

    if (routes.MANAGE_PATIENTS === location.pathname) {
      getRxLinkedPrograms();
    }

    return () => {
      selectClinic('', '');
      Program.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearSelected();
    if (location.pathname === routes.MANAGE_CLINICIANS) {
      getClinics();
    }

    if (location.pathname === routes.MANAGE_PATIENTS) {
      if (!Program.get()) {
        setClinics([]);
      }

      getRxLinkedPrograms();
    }
  }, [clearSelected, getRxLinkedPrograms, location.pathname]);

  const isManagePatientsPath = location.pathname === routes.MANAGE_PATIENTS;
  const isSelectClinicDisabled = !Program.get()?.name && isManagePatientsPath;

  return (
    <div data-testid={`${isLoading ? 'loading' : 'loaded'}`}>
      <ul styleName="tabs">
        {isManageLocationsTabShown && (
          <li
            styleName={clsx('tab', {
              active: location.pathname === routes.MANAGE_LOCATIONS,
            })}
          >
            <NavLink
              data-testid="tabbar-nav-link-manage-locations"
              to={routes.MANAGE_LOCATIONS}
              activeClassName={`${styles.active}`}
            >
              Manage Locations
            </NavLink>
          </li>
        )}
        {isManageCliniciansTabShown && (
          <li
            styleName={clsx('tab', {
              active: location.pathname === routes.MANAGE_CLINICIANS,
            })}
          >
            <NavLink
              data-testid="tabbar-nav-link-manage-clinicians"
              to={routes.MANAGE_CLINICIANS}
              activeClassName={`${styles.active}`}
            >
              Manage Clinicians
            </NavLink>
          </li>
        )}
        {isManagePatientsTabShown && (
          <li
            styleName={clsx('tab', {
              active: location.pathname === routes.MANAGE_PATIENTS,
            })}
          >
            <NavLink
              data-testid="tabbar-nav-link-manage-patients"
              to={routes.MANAGE_PATIENTS}
              activeClassName={`${styles.active}`}
            >
              Manage Patients
            </NavLink>
          </li>
        )}
      </ul>
      {showClinicPulldown() && (
        // TODO: [WB-2478] Move this to Manage Clinicians and Manage Patients
        <div styleName="bulk-upload-wrapper">
          <div styleName="select-clinic-wrapper">
            <div styleName="select-wrapper">
              {isManagePatientsPath && (
                <div>
                  <div styleName="label">Product:</div>
                  <SelectProgram
                    programs={programs}
                    onChange={handleChangeProgram}
                    setProgram={setProgram}
                  />
                </div>
              )}
              <div>
                <div styleName="label">Clinic:</div>
                <SelectLocation
                  disabled={isManagePatientsPath && isSelectClinicDisabled}
                  options={clinics}
                  onChangeLocation={selectClinic}
                  placeholder="Select Location"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tabbar;
